import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import { AutoFixHigh as GenerateIcon } from '@mui/icons-material';
import api from '../../utils/api';
import AIModelSelect, { DEFAULT_MODELS } from '../common/AIModelSelect';

const GeneratePipelineDialog = ({ open, onClose, onPipelineGenerated }) => {
  const [description, setDescription] = useState('');
  const [provider, setProvider] = useState('openai');
  const [model, setModel] = useState(DEFAULT_MODELS.openai);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleGenerate = async () => {
    if (description.trim().length < 10) {
      setError('Description must be at least 10 characters.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await api.post('/pipeline-generate', {
        description: description.trim(),
        provider,
        model,
      });
      onPipelineGenerated(response.data);
      setDescription('');
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Generation failed';
      setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <GenerateIcon color="primary" />
        AI Generate Pipeline
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Describe the pipeline you want to build in natural language. The AI will generate
          nodes, connections, and configurations for you.
        </Typography>

        <TextField
          label="Pipeline Description"
          placeholder='e.g. "Read customer data from CSV, filter inactive customers, classify sentiment using AI, save results to PostgreSQL"'
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          rows={4}
          margin="normal"
          disabled={isLoading}
          inputProps={{ maxLength: 2000 }}
          helperText={`${description.length}/2000`}
        />

        <Box sx={{ mt: 2 }}>
          <AIModelSelect
            provider={provider}
            model={model}
            onProviderChange={setProvider}
            onModelChange={setModel}
            disabled={isLoading}
            size="medium"
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleGenerate}
          variant="contained"
          disabled={isLoading || description.trim().length < 10}
          startIcon={isLoading ? <CircularProgress size={20} /> : <GenerateIcon />}
        >
          {isLoading ? 'Generating...' : 'Generate'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GeneratePipelineDialog;
